/**
 * passage.jsx — render a shelved passage as what it IS, rather than as one long line.
 *
 * FOUNDER, 2026-08-16, after seven chapters had been brought to depth: the reading surface was
 * flattening every command block into the sentence around it. A learner opening chapter 1 read
 *
 *     Check: ```bash node --version npm --version ``` **You should see** — two version numbers
 *
 * which is not a procedure, it is a paragraph wearing the punctuation of one. The whole value of the
 * depth pass — a command you can copy, and what you should see when you run it — arrived as mush at
 * the only place a learner ever looks. The markdown was correct in the file and correct in the
 * compiled shelf; it was correct everywhere except on screen.
 *
 * ── WHY THIS IS SIX CONSTRUCTS AND NOT A MARKDOWN LIBRARY ─────────────────────────────────────
 * This app has zero runtime dependencies and the build should not quietly undo that (see
 * vite.config.js). More importantly, a general parser would be answering a question nobody asked:
 * we COMPILE this shelf, so what is in it is knowable rather than arbitrary. Measured across all
 * 173 entries and 2,001 lines on 2026-08-16:
 *
 *     fenced blocks 100 lines · tables 85 · bullets 145 · bold 474 · inline code 288
 *     headings 0 · blockquotes 0 · links 0 · numbered lists 0 · italics 0 · rules 0
 *
 * Six constructs, and the six are handled exactly. gate-passage-render.mjs re-measures that list on
 * every run, so the day a chapter introduces a seventh the gate fails LOUDLY rather than the
 * surface rendering literal markup at a reader. An unhandled construct must be a failure, not a
 * silent passthrough — same law the registers rest on.
 *
 * ── NOTHING MAY BE DROPPED ────────────────────────────────────────────────────────────────────
 * The CodeAI101 seeder once deleted every blockquote line while stripping a marker and lost 694
 * words of worked examples before anyone noticed. A renderer can do the same thing more quietly.
 * So `blocks()` is a pure function, exported and tested in node, and the gate asserts that the
 * letters and digits coming out of it are exactly the letters and digits that went in.
 */

/** Inline: `code` and **bold**, in one pass, keeping every segment including the ones that match
 *  nothing. Splitting with a capturing group means the text BETWEEN matches survives — the obvious
 *  implementation with .replace() drops it. */
export function inlineParts(s) {
  const out = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0, m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: s.slice(last, m.index) });
    const tok = m[0];
    out.push(tok.startsWith('`')
      ? { kind: 'code', text: tok.slice(1, -1) }
      : { kind: 'bold', text: tok.slice(2, -2) });
    last = m.index + tok.length;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

/**
 * Block parse. Pure, no React, so the gate can run it in node and check the invariant directly.
 * Returns [{ kind: 'pre'|'table'|'list'|'p', ... }].
 */
export function blocks(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let i = 0;
  const flushPara = (buf) => { if (buf.length) out.push({ kind: 'p', text: buf.join(' ').trim() }); };
  let para = [];

  while (i < lines.length) {
    const line = lines[i];

    /* A FENCE OPENS AND MUST CLOSE. An unterminated fence used to be possible only in a hand-written
       file; the shelf is compiled, so if it happens it is a compiler fault. Treated as running to
       the end rather than discarded — losing the text would be the worse of the two failures. */
    if (/^```/.test(line)) {
      flushPara(para); para = [];
      const lang = line.slice(3).trim();
      const code = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i += 1; }
      i += 1;                                        // step past the closing fence
      out.push({ kind: 'pre', lang, text: code.join('\n') });
      continue;
    }

    if (/^\s*\|/.test(line)) {
      flushPara(para); para = [];
      const rows = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        const cells = lines[i].trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
        /* The |---|---| separator carries no content. Dropped by SHAPE — every cell is dashes and
           colons — rather than by position, because a table without a header row is legal markdown
           and the second line is not always the separator. */
        if (!cells.every((c) => /^:?-{1,}:?$/.test(c))) rows.push(cells);
        i += 1;
      }
      if (rows.length) out.push({ kind: 'table', rows });
      continue;
    }

    if (/^\s*-\s+/.test(line)) {
      flushPara(para); para = [];
      const items = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ''));
        i += 1;
      }
      out.push({ kind: 'list', items });
      continue;
    }

    if (line.trim() === '') { flushPara(para); para = []; i += 1; continue; }

    para.push(line.trim());
    i += 1;
  }
  flushPara(para);
  return out;
}

/** The constructs this renderer handles. The gate compares the shelf against exactly this list. */
export const HANDLED = Object.freeze(['pre', 'table', 'list', 'p']);

